import { SendIcon, SlidersHorizontal, Square } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AutoResizeTextarea } from '@/shared/components/auto-resize-textarea';
import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils';
import { RingSpinner } from '@/shared/components/loader/ring-spinner';
import { useDiscoveryStore } from '../../stores/discovery-store';
import {
  DiscoverySelectedFilters,
  type DiscoverySelectedFilterKey,
} from './discovery-selected-filters';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/shadcn/popover';
import { CreatorsInlineFilters } from '../search-results/result-phase.filters';
import {
  DiscoveryAssistantMode,
  useAnalyzeCommonality,
  useAnalyzeDiscoveryPrompt,
  type AssistantSearchMessage,
  type DiscoveryMessage,
  type FilterConditions,
} from '@/entities/discovery';
import { Scene, Stage } from '@/shared/components/stage/stage';
import { useSeedPostStore, type SeedPost } from '../../stores/seed-post-store';
import {
  buildAssistantLoadingMessage,
  buildUserPostsMessage,
  buildUserPromptMessage,
} from '../discovery-message/build-message.helpers';
import {
  useDiscoveryMessages,
  useDiscoveryMessagesActions,
} from '../../stores/discovery-message-store';
import { SeedPostImage } from '../seed-post/seed-post-image';
import { useStopDiscoverySearch } from '../../hooks/use-stop-discovery-search';

export const DISCOVERY_CHAT_INPUT_ID = 'discovery-chat-input';

interface Props {
  submitLabel?: string;
  submitWhenEmpty?: boolean;
  canSubmitWithoutText?: boolean;
  emptySubmitLabel?: string;
  interruptionSubmit?: boolean;
  sessionId: string;
}

export function ChatPanelInput({ sessionId }: Props) {
  const { session, filters, setFilters, tempFilter, setTempFilter, isSearching } =
    useDiscoveryStore();
  const { stopSearch } = useStopDiscoverySearch({
    sessionId,
    searchId: session?.current_search_history_id,
  });
  const messages = useDiscoveryMessages();
  const { seedPosts, removeSeedPost, clearSeedPosts } = useSeedPostStore();
  const { appendMessages, replaceMessages } = useDiscoveryMessagesActions();
  const analyzePromptMutation = useAnalyzeDiscoveryPrompt();
  const analyzeCommonalityMutation = useAnalyzeCommonality();
  const latestAssistantSearchFilters = useMemo(() => {
    return getLatestAssistantSearchFilters(messages);
  }, [messages]);
  const [executedFilter, setExecutedFilter] = useState<FilterConditions>(
    latestAssistantSearchFilters ?? filters,
  );
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [internalValue, setInternalValue] = useState('');
  const setFiltersRef = useRef(setFilters);
  const isEmpty = internalValue.trim().length === 0;

  useEffect(() => {
    setFiltersRef.current = setFilters;
  }, [setFilters]);

  useEffect(() => {
    if (!latestAssistantSearchFilters) return;

    setExecutedFilter(latestAssistantSearchFilters);
    setTempFilter(latestAssistantSearchFilters);
    setFiltersRef.current(latestAssistantSearchFilters);
  }, [latestAssistantSearchFilters, setTempFilter]);

  const handleSubmit = async () => {
    if (seedPosts.length > 0) {
      const submittedFilters = tempFilter;
      setExecutedFilter(submittedFilters);
      setTempFilter(submittedFilters);
      setFilters(submittedFilters);

      appendMessages([
        buildAssistantLoadingMessage(sessionId),
        buildUserPostsMessage(
          sessionId,
          seedPosts.map((post) => ({
            post_id: post.id,
            poster_url: post.posterUrl,
          })),
          submittedFilters,
        ),
      ]);
      clearSeedPosts();
      const res = await analyzeCommonalityMutation.mutateAsync({
        discoverySessionId: sessionId,
        request: {
          post_id_list: seedPosts.map((p) => p.id),
          filter_conditions: submittedFilters,
        },
      });
      replaceMessages([res.assistant_message, res.user_message]);
    } else {
      const trimmed = internalValue.trim();
      if (trimmed) {
        const submittedFilters = tempFilter;
        setExecutedFilter(submittedFilters);
        setTempFilter(submittedFilters);
        setFilters(submittedFilters);

        appendMessages([
          buildAssistantLoadingMessage(sessionId),
          buildUserPromptMessage(sessionId, trimmed, submittedFilters),
        ]);
        setInternalValue('');
        const res = await analyzePromptMutation.mutateAsync({
          discoverySessionId: sessionId,
          request: {
            message: trimmed,
            filter_conditions: submittedFilters,
          },
        });
        replaceMessages([res.assistant_message, res.user_message]);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isSearching) {
        handleStopAndSubmit();
      } else {
        handleSubmit();
      }
    }
  };

  const handleStopAndSubmit = () => {
    stopSearch();
    void handleSubmit();
  };

  const handleResetDraftFilter = useCallback(
    (filterKey: DiscoverySelectedFilterKey) => {
      setTempFilter((currentFilters) => {
        const nextFilters = { ...currentFilters };

        if (filterKey === 'platform') {
          nextFilters.platform = executedFilter.platform;
        }

        if (filterKey === 'time-range') {
          nextFilters.start_date = executedFilter.start_date;
          nextFilters.end_date = executedFilter.end_date;
        }

        if (filterKey === 'followers') {
          nextFilters.engagement = normalizeCreatorEngagement({
            ...(currentFilters.engagement ?? {}),
            followers_min: executedFilter.engagement?.followers_min ?? null,
            followers_max: executedFilter.engagement?.followers_max ?? null,
          });
        }

        if (filterKey === 'er') {
          nextFilters.engagement = normalizeCreatorEngagement({
            ...(currentFilters.engagement ?? {}),
            er_min: executedFilter.engagement?.er_min ?? null,
            er_max: executedFilter.engagement?.er_max ?? null,
          });
        }

        return nextFilters;
      });
    },
    [executedFilter, setTempFilter],
  );

  const handleTempFilterChange = useCallback(
    (nextFilters: Partial<FilterConditions>) => {
      setTempFilter((currentFilters) => ({
        ...currentFilters,
        ...nextFilters,
      }));
    },
    [setTempFilter],
  );

  const hideInputArea = seedPosts.length > 0;

  const searchDisabled: boolean = useMemo(() => {
    const hasPrompt = !isEmpty;
    const hasSeedPost = seedPosts.length > 0;

    return !hasPrompt && !hasSeedPost;
  }, [isEmpty, seedPosts.length]);

  return (
    <div
      className={cn('border-input bg-background w-full rounded-xl border px-3.5 py-2.5 shadow-sm')}
    >
      {seedPosts.length > 0 && (
        <div className="mb-2.5">
          <ComposerAttachments
            seedPosts={seedPosts}
            removeSeedPost={removeSeedPost}
            clearSeedDraft={clearSeedPosts}
          />
        </div>
      )}

      {hideInputArea ? null : (
        <div className={cn('min-h-10 opacity-100 transition-opacity', isEmpty && 'opacity-70')}>
          <AutoResizeTextarea
            id={DISCOVERY_CHAT_INPUT_ID}
            value={internalValue}
            onChange={(e) => setInternalValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe a creator"
            className={cn(
              'text-foreground w-full border-0 bg-transparent px-0 py-0 text-sm shadow-none outline-none focus-visible:ring-0',
              'placeholder:text-muted-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            maxHeight={132}
            minHeight={40}
          />
        </div>
      )}

      <div
        className={cn('flex items-center justify-between gap-3', hideInputArea ? 'mt-0' : 'mt-2')}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <div className="shrink-0">
            <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0 rounded-full"
                >
                  <SlidersHorizontal className="size-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" side="top" className="w-88 p-4">
                <div className="mb-3">
                  <p className="text-foreground text-sm font-semibold">Search filters</p>
                </div>
                <CreatorsInlineFilters
                  filters={tempFilter}
                  onFiltersChange={handleTempFilterChange}
                  close={() => setFilterPopoverOpen(false)}
                  applyLabel="Apply filter"
                  className="w-full"
                />
              </PopoverContent>
            </Popover>
          </div>
          <DiscoverySelectedFilters
            committedFilters={executedFilter}
            filters={tempFilter}
            onResetFilter={handleResetDraftFilter}
          />
        </div>

        <div className="flex items-center gap-2">
          <Stage>
            <Scene status={isSearching}>
              <Button
                onClick={handleStopAndSubmit}
                size="sm"
                className={cn('shrink-0 rounded-full')}
                disabled={searchDisabled}
              >
                <Square className="size-3.5 fill-current" />
                Stop and search
              </Button>
            </Scene>
            <Scene default>
              <Button
                onClick={handleSubmit}
                size={'icon'}
                className={cn('size-8 shrink-0 rounded-full')}
                disabled={searchDisabled}
              >
                <SendIcon className="size-3.5" />
              </Button>
            </Scene>
          </Stage>
        </div>
      </div>
    </div>
  );
}

export const ComposerAttachments = ({
  seedPosts,
  removeSeedPost,
  clearSeedDraft,
  isLoading,
}: {
  seedPosts: SeedPost[];
  removeSeedPost: (postId: string) => void;
  clearSeedDraft: () => void;
  isLoading?: boolean;
}) => (
  <div className="flex flex-col gap-2.5">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-foreground text-xs font-medium">
          {seedPosts.length} post{seedPosts.length > 1 ? 's' : ''} selected
        </p>
        <div className="mt-2">
          <SeedPostImage posts={seedPosts} size="composer" onRemovePost={removeSeedPost} />
        </div>
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={clearSeedDraft} disabled={isLoading}>
        Clear
      </Button>
    </div>

    {isLoading && (
      <div className="flex items-center gap-2 px-0.5">
        <RingSpinner className="size-4" />
        <p className="text-muted-foreground text-xs">Finding matching keywords...</p>
      </div>
    )}
  </div>
);

function normalizeCreatorEngagement(
  engagement: FilterConditions['engagement'],
): FilterConditions['engagement'] | null {
  if (!engagement) return null;

  return Object.values(engagement).some((value) => value != null) ? engagement : null;
}

function getLatestAssistantSearchFilters(messages: DiscoveryMessage[]) {
  return messages
    .filter(isAssistantSearchMessage)
    .sort((left, right) => right.created_at.localeCompare(left.created_at))[0]?.content
    .search_conditions.filter_conditions;
}

function isAssistantSearchMessage(message: DiscoveryMessage): message is AssistantSearchMessage {
  return message.role === 'assistant' && message.assistant_mode === DiscoveryAssistantMode.SEARCH;
}
